import _ from "lodash";
import {
  getElementTypeLabels,
  updateTemplate,
  shouldConvertNonPortedFieldsToPorted,
} from "IBPlanner/modules/IBPlannerModule";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";
import ACLStore from "lib/aclStore";
import { getPropertyValue } from "Fields/FieldUtils";
import {
  TEMPLATE_FIELD_DISCIPLINARY_CONTENT,
  TEMPLATE_FIELD_DISCIPLINARY_LE_QUESTION,
  TEMPLATE_FIELD_DISCIPLINARY_LEARNING_STANDARDS_MYP,
  TEMPLATE_FIELD_DISCIPLINARY_OBJECTIVES_MYP,
  TEMPLATE_FIELD_DISCIPLINARY_RELATED_CONCEPTS_MYP,
  TEMPLATE_FIELD_LEARNING_STANDARDS_MYP,
  TEMPLATE_FIELD_OBJECTIVES_MYP,
  TEMPLATE_FIELD_RELATED_CONCEPTS_MYP,
} from "Constants/stringConstants";

export const isSubjectStandard = ({ elementType }) => {
  return _.includes(
    [
      "DP_SUBJECT_STANDARD",
      "PYP_LEARNING_STANDARD",
      "UBD_LEARNING_STANDARD",
      "MYP_LEARNING_STANDARD",
    ],
    elementType
  );
};

export const mypStandAloneDisciplinaryFields = [
  TEMPLATE_FIELD_DISCIPLINARY_CONTENT,
  TEMPLATE_FIELD_DISCIPLINARY_LE_QUESTION,
];

export const disciplinaryPlannerFields = [
  ...mypStandAloneDisciplinaryFields,
  TEMPLATE_FIELD_DISCIPLINARY_LEARNING_STANDARDS_MYP,
  TEMPLATE_FIELD_DISCIPLINARY_OBJECTIVES_MYP,
  TEMPLATE_FIELD_DISCIPLINARY_RELATED_CONCEPTS_MYP,
];

export const mypDisciplinaryFields = [
  TEMPLATE_FIELD_OBJECTIVES_MYP,
  TEMPLATE_FIELD_RELATED_CONCEPTS_MYP,
  TEMPLATE_FIELD_LEARNING_STANDARDS_MYP,
  ...disciplinaryPlannerFields,
];

const extractFieldsFromModule = ({
  moduleId,
  moduleList,
  stageList,
  stepList,
  fieldList,
  fieldListV2,
}) => {
  const fieldsToAdd = [];
  const module = _.get(moduleList, moduleId);

  if (!module) return fieldsToAdd;

  const stages = _.get(module, "stages", []);

  _.forEach(stages, stageId => {
    const stage = _.get(stageList, stageId);
    if (!stage) return;

    const steps = _.get(stage, "steps", []);

    _.forEach(steps, stepId => {
      const step = _.get(stepList, stepId);
      if (!step) return;

      // Skip UNIT_DETAILS step
      if (_.isEqual(stepId, "UNIT_DETAILS")) return;

      const fields = _.get(step, "fields", []);

      _.forEach(fields, fieldId => {
        const isPortedField = _.has(fieldListV2, fieldId);
        const fieldObj = isPortedField
          ? _.get(fieldListV2, fieldId)
          : _.get(fieldList, fieldId);

        if (!fieldObj) return;

        // Skip IBReflection fields
        let type = _.get(fieldObj, "type");
        if (isPortedField) {
          type = getPropertyValue({
            propertyPath: "uiType",
            fieldObj,
          });
        }

        if (_.isEqual(type, "IBReflection")) return;

        let elementType = _.get(fieldObj, "config.plannerElementConfig.type");

        if (isPortedField) {
          elementType = getPropertyValue({
            propertyPath: "plannerElementConfigType",
            fieldObj,
          });
        }
        if (stepId.includes("DISCIPLINARY_")) {
          return;
        }
        // Skip subject standard fields
        if (isSubjectStandard({ elementType })) return;

        fieldsToAdd.push({
          fieldUID: fieldId,
          isSelected: false,
          isSubjectSpecificDynamic: false,
          shouldDisplay: true,
        });
      });
    });
  });

  return fieldsToAdd;
};

const getCombinedCourseOverview = ({ templates, isLearningCoursesEnabled }) => {
  const combinedCourseOverview = [],
    uniqueElementTypes = [];
  const enableAllFields = getEnableAllFieldsInCourseOverview();
  const isNonPortedFieldsConvertedToPortedFields = shouldConvertNonPortedFieldsToPorted();

  _.forEach(templates, template => {
    const body = template?.body;
    const courseOverview = _.get(body, "courseOverview", []);

    if (courseOverview) {
      _.forEach(courseOverview, field => {
        if (!_.find(combinedCourseOverview, { fieldUID: field?.fieldUID })) {
          let fieldItem = _.get(
            _.get(body, "fieldListV2", {}),
            field?.fieldUID
          );

          if (_.isEmpty(fieldItem)) {
            fieldItem = _.get(_.get(body, "field_list", {}), field?.fieldUID);
          }
          if (_.isEmpty(fieldItem)) {
            return;
          }
          // we will later need element type to combine fields
          let elementType = _.get(
            fieldItem,
            "config.plannerElementConfig.type"
          );

          let isDisciplinary = _.get(fieldItem, "config.isDisciplinary", false);

          let dynamicSubject = _.get(fieldItem, "dynamicSubject");

          if (isNonPortedFieldsConvertedToPortedFields) {
            isDisciplinary = getPropertyValue({
              propertyPath: "isDisciplinary",
              fieldObj: fieldItem,
            });

            elementType = getPropertyValue({
              propertyPath: "plannerElementConfigType",
              fieldObj: fieldItem,
            });

            dynamicSubject = getPropertyValue({
              propertyPath: "dynamicSubject",
              fieldObj: fieldItem,
            });
          }

          const isPortedAndValidField =
            isNonPortedFieldsConvertedToPortedFields &&
            _.has(_.get(body, "fieldListV2", {}), field.fieldUID);

          let updatedField = {
            ...field,
            elementType,
            isDisciplinary,
            dynamicSubject,
            shouldDisplay: _.includes(
              mypStandAloneDisciplinaryFields,
              field.fieldUID
            )
              ? isDisciplinary
              : !isDisciplinary,
            isPortedField: isPortedAndValidField,
            hideCoveredLabel: _.isEmpty(elementType),
            isNotPlannerElement: isPortedAndValidField,
          };
          if (
            field.fieldUID === "conceptsQuestion" ||
            field.fieldUID === "actionQuestion"
          ) {
            updatedField = {
              ...updatedField,
              shouldDisplay: false,
              isNotPlannerElement: true,
            };
          }

          if (!_.includes(uniqueElementTypes, elementType))
            uniqueElementTypes.push(elementType);
          combinedCourseOverview.push(updatedField);
        }
      });
    }

    if (enableAllFields) {
      // Append fields from the first module (planner)
      const modules = _.get(body, "modules", []);
      const moduleList = _.get(body, "module_list", {});
      const stageList = _.get(body, "stage_list", {});
      const stepList = _.get(body, "step_list", {});
      const fieldList = _.get(body, "field_list", {});
      const fieldListV2 = _.get(body, "fieldListV2", {});

      if (!_.isEmpty(modules)) {
        const firstModuleId = modules[0];
        const fieldsToAdd = extractFieldsFromModule({
          moduleId: firstModuleId,
          moduleList,
          stageList,
          stepList,
          fieldList,
          fieldListV2,
        });

        _.forEach(fieldsToAdd, field => {
          // Only add if not already present to avoid duplicates
          if (!_.find(combinedCourseOverview, { fieldUID: field.fieldUID })) {
            combinedCourseOverview.push({
              ...field,
              hideByDefault: true,
              hideCoveredLabel: true,
              isNotPlannerElement: true,
              isPortedField: _.has(fieldListV2, field.fieldUID),
            });
          }
        });
      }
    }
  });

  // take care of interdisciplinary ones that do not display on their own
  if (isLearningCoursesEnabled) {
    _.forEach(combinedCourseOverview, field => {
      if (_.includes(mypDisciplinaryFields, field?.fieldUID)) {
        if (!_.has(field, "config")) field["config"] = {};
        field["config"]["interdisciplinaryUID"] = field?.fieldUID;
      }
    });
  } else {
    _.forEach(
      _.filter(combinedCourseOverview, ["isDisciplinary", true]),
      interdisciplinaryField => {
        if (
          _.includes(
            mypStandAloneDisciplinaryFields,
            interdisciplinaryField?.fieldUID
          )
        ) {
          if (!_.has(interdisciplinaryField, "config"))
            interdisciplinaryField["config"] = {};
          interdisciplinaryField["config"]["interdisciplinaryUID"] =
            interdisciplinaryField?.fieldUID;
        } else {
          const subjectSpecificField = _.find(
            combinedCourseOverview,
            field =>
              !_.isEqual(field?.fieldUID, interdisciplinaryField?.fieldUID) &&
              _.isEqual(field?.elementType, interdisciplinaryField?.elementType)
          );
          if (subjectSpecificField) {
            if (!_.has(subjectSpecificField, "config"))
              subjectSpecificField["config"] = {};

            subjectSpecificField["config"]["interdisciplinaryUID"] =
              interdisciplinaryField?.fieldUID;
          }
        }
      }
    );
  }

  return combinedCourseOverview;
};

const getPlannerFieldForCourseOverview = ({
  gradeTemplatesMap,
  unitPlanTemplates,
  subjects,
  subjectType,
  curriculumType,
  t,
  fieldLists,
  isCourseBasedGradingEnabled,
  plannerElementSets,
  isLearningCoursesEnabled,
}) => {
  const enableAllFields = getEnableAllFieldsInCourseOverview();
  const isNonPortedFieldsConvertedToPorted = shouldConvertNonPortedFieldsToPorted();
  const updatedTemplates = _.map(unitPlanTemplates, template => {
    const unitType = template?.unitType;
    let updatedTemplate;
    // for all fields we add fieldListV2 in updateTemplate
    if (enableAllFields) {
      const fieldListV2 = _.get(fieldLists, template.id, {});
      updatedTemplate = updateTemplate({
        t,
        template,
        curriculumType,
        unitType,
        resolvedSubjectNodes: subjects,
        shouldUpdateViewTypeInSinglePage: true,
        isCourseBasedGradingEnabled,
        isSinglePageAndNewUnitView: true,
        fieldListV2,
        enableAllFields,
        plannerElementSets,
      });
    } else {
      updatedTemplate = updateTemplate({
        t,
        template,
        curriculumType,
        unitType,
        resolvedSubjectNodes: subjects,
        shouldUpdateViewTypeInSinglePage: true,
        isCourseBasedGradingEnabled,
        isSinglePageAndNewUnitView: true,
        plannerElementSets,
      });
    }

    return updatedTemplate;
  });

  // to fetch labels of field headers, we'll check every template
  const templateFieldLists = _.map(updatedTemplates, "body.field_list");
  let portedTemplateFieldLists;
  if (enableAllFields || isNonPortedFieldsConvertedToPorted) {
    portedTemplateFieldLists = _.map(updatedTemplates, "body.fieldListV2");
  }

  // use current grade's templates to generate course overview fields
  const gradeTemplates = _.map(gradeTemplatesMap, template =>
    _.find(updatedTemplates, { id: template.id })
  );

  const courseOverview = getCombinedCourseOverview({
    templates: gradeTemplates,
    isLearningCoursesEnabled,
  });

  if (courseOverview) {
    const courseOverviewFields = _.reduce(
      courseOverview,
      (result, item) => {
        const fieldUID = item?.fieldUID;

        let isNotPlannerElement, isPortedField;
        if (enableAllFields || isNonPortedFieldsConvertedToPorted) {
          isNotPlannerElement = _.get(item, "isNotPlannerElement", false);
          isPortedField = _.get(item, "isPortedField", false);
        }

        let gradeFieldObj = _.find(templateFieldLists, fieldList =>
          _.has(fieldList, fieldUID)
        )?.[fieldUID];

        // if non-ported fields are converted to ported
        // gradeFieldObj will be empty and the below case will be considered
        if (isPortedField) {
          gradeFieldObj = _.find(portedTemplateFieldLists, fieldList =>
            _.has(fieldList, fieldUID)
          )?.[fieldUID];
        }
        // if current subject type doesn't have this field
        // or no templates in this grade has this field then skip
        if (
          (_.has(item, "subjectsToSkip") &&
            _.includes(item.subjectsToSkip, subjectType)) ||
          _.isEmpty(gradeFieldObj)
        )
          return result;

        // store fieldObjects from each unitPlan template
        // this will be used when clicking on a unit plan field edit button
        const fieldObjects = _.reduce(
          updatedTemplates,
          (result, template) => {
            const body = template?.body;
            if (body) {
              const interdisciplinaryUID = _.get(
                item,
                "config.interdisciplinaryUID"
              );
              if (enableAllFields) {
                if (isPortedField) {
                  if (_.has(body, ["fieldListV2", fieldUID])) {
                    const fieldObjFromTemplate = _.get(body, [
                      "fieldListV2",
                      fieldUID,
                    ]);

                    if (isNotPlannerElement) {
                      result[template.id] = {
                        ...fieldObjFromTemplate,
                        showReflection: {
                          ...fieldObjFromTemplate.showReflection,
                          value: false,
                        },
                      };
                    }
                  }
                } else {
                  if (_.has(body, ["field_list", fieldUID])) {
                    const fieldObjFromTemplate = _.get(body, [
                      "field_list",
                      fieldUID,
                    ]);
                    // For fields from extractFieldsFromModule, disable showReflection
                    if (isNotPlannerElement) {
                      result[template.id] = {
                        ...fieldObjFromTemplate,
                        config: {
                          ..._.get(fieldObjFromTemplate, "config", {}),
                          showReflection: false,
                        },
                      };
                    } else {
                      result[template.id] = fieldObjFromTemplate;
                    }
                  }
                  // MYP interdisciplinary fields are displayed under subject specific fields
                  else if (_.has(body, ["field_list", interdisciplinaryUID])) {
                    result[template.id] = _.get(body, [
                      "field_list",
                      interdisciplinaryUID,
                    ]);
                  }
                }
              } else {
                // if enableAllFields is false
                // isPortedField will only be true if non-ported fields are converted to portedFields
                if (isPortedField) {
                  if (_.has(body, ["fieldListV2", fieldUID])) {
                    const fieldObjFromTemplate = _.get(body, [
                      "fieldListV2",
                      fieldUID,
                    ]);

                    if (isNotPlannerElement) {
                      result[template.id] = {
                        ...fieldObjFromTemplate,
                        showReflection: {
                          ...fieldObjFromTemplate.showReflection,
                          value: false,
                        },
                      };
                    }
                  }
                } else {
                  if (_.has(body, ["field_list", fieldUID])) {
                    result[template.id] = _.get(body, ["field_list", fieldUID]);
                  }
                  // MYP interdisciplinary fields are displayed under subject specific fields
                  else if (_.has(body, ["field_list", interdisciplinaryUID])) {
                    result[template.id] = _.get(body, [
                      "field_list",
                      interdisciplinaryUID,
                    ]);
                  }
                }
              }
            }

            return result;
          },
          {}
        );
        let fieldLabel = _.get(gradeFieldObj, "config.label", "");

        let elementType = _.get(
          item,
          "elementType",
          _.get(gradeFieldObj, "config.plannerElementConfig.type")
        );

        let dynamicSubject = _.get(
          item,
          "dynamicSubject",
          _.get(gradeFieldObj, "dynamicSubject")
        );

        if (isPortedField) {
          fieldLabel = getPropertyValue({
            propertyPath: "label",
            fieldObj: gradeFieldObj,
          });

          elementType = getPropertyValue({
            propertyPath: "plannerElementConfigType",
            fieldObj: gradeFieldObj,
          });

          dynamicSubject = getPropertyValue({
            fieldObj: gradeFieldObj,
            propertyPath: "dynamicSubject",
          });
        }

        const itemConfig = _.get(item, "config", {});

        const courseOverviewField = {
          ...item,
          ...itemConfig,
          gradeFieldObj,
          fieldObjects,
          label: fieldLabel,
          elementType,
          dynamicSubject,
        };

        // incase the field has interdisciplinary counterparts that change with subject
        // push them into courseOverviewFields as well but don't display them, only used for fetching fieldObj
        if (_.has(itemConfig, "interdisciplinaryUID")) {
          const interdisciplinaryUID = itemConfig.interdisciplinaryUID;
          const interdisciplinaryTemplates = _.filter(updatedTemplates, [
            "unitType",
            "ibMYPInterdisciplinary",
          ]);

          _.forEach(subjects, subject => {
            const fieldUIDforInterdisciplinarySubject = `${interdisciplinaryUID}_${subject.id}`;
            const interdisciplinaryFieldObjects = _.reduce(
              interdisciplinaryTemplates,
              (result, template) => {
                const body = template?.body;

                // check fields in both fieldListV2 and field_list
                // fieldListV2 takes priority to cover non-ported fields converted to ported fields case
                let interdisciplinarySubjectField = _.get(body, [
                  "fieldListV2",
                  fieldUIDforInterdisciplinarySubject,
                ]);

                if (_.isEmpty(interdisciplinarySubjectField)) {
                  interdisciplinarySubjectField = _.get(body, [
                    "field_list",
                    fieldUIDforInterdisciplinarySubject,
                  ]);
                }

                if (body && !_.isEmpty(interdisciplinarySubjectField))
                  result[template.id] = interdisciplinarySubjectField;

                return result;
              },
              {}
            );
            result.push({
              // interdisciplinary fields are only displayed under their respective disciplinary field
              shouldDisplay: false,
              // a new field id is stored in unit plan for each subject's elements
              fieldUID: fieldUIDforInterdisciplinarySubject,
              actualUID: fieldUID,
              fieldObjects: interdisciplinaryFieldObjects,
              label: fieldLabel,
              hideCoveredLabel: _.get(item, "hideCoveredLabel", false),
              elementType,
              isPortedField: _.some(portedTemplateFieldLists, fieldList =>
                _.has(fieldList, fieldUIDforInterdisciplinarySubject)
              ),
            });
          });

          courseOverviewField["interdisciplinaryFields"] = _.map(
            subjects,
            subject => `${interdisciplinaryUID}_${subject.id}`
          );
        }
        result.push(courseOverviewField);

        return result;
      },
      []
    );

    return {
      courseOverviewFields,
      fieldUIDs: getDefaultFieldUIDs({
        courseOverviewFields,
        excludeHidden: false,
      }),
    };
  }

  return { courseOverviewFields: [], fieldUIDs: [] };
};

export const getPlannerFieldForCourseOverviewMemoized = _.memoize(
  params => getPlannerFieldForCourseOverview(params),
  props => JSON.stringify(props)
);

const getDefaultFieldUIDs = ({
  courseOverviewFields,
  excludeHidden = true,
}) => {
  if (!excludeHidden) {
    return [
      ..._.map(courseOverviewFields, "fieldUID"),
      "hours",
      "subjectLevel",
      "subjects",
      "subjectGroup",
      "objectiveLevel",
      "unitType",
      "theme",
    ];
  }

  const fieldUIDs = _.reduce(
    courseOverviewFields,
    (result, field) => {
      if (!_.get(field, "hideByDefault", false)) {
        result.push(field.fieldUID);
      }
      return result;
    },
    []
  );

  return fieldUIDs;
};

export const getDefaultFieldUIDsMemoized = _.memoize(
  params => getDefaultFieldUIDs(params),
  props => JSON.stringify(props)
);

const getFieldOptions = props => {
  const objectiveLevelNodes = _.map(
    _.get(
      _.find(props?.plannerElementNodeSets, { type: "MYP_OBJECTIVES" }),
      "plannerElementNodeSetWrapperV2.publishedData.edges",
      []
    ),
    "node"
  );

  return getPlannerFieldOptions({ ...props, objectiveLevelNodes });
};

export const getFieldOptionsMemoized = _.memoize(
  params => getFieldOptions(params),
  props => JSON.stringify(props)
);

const getNodesFromPlannerSets = ({ plannerElements, type }) => {
  return _.get(_.find(plannerElements, { type }), "nodes", []);
};

export const getNodesFromPlannerSetsMemoized = _.memoize(
  params => getNodesFromPlannerSets(params),
  props => JSON.stringify(props)
);

const getSubjectStandardColumns = ({
  subjectStandardField,
  subjects,
  t,
  data,
  elementType,
  courseSubjects,
  enableAllFields,
  isPYP,
}) => {
  const fieldUID = subjectStandardField?.fieldUID;
  const interdisciplinaryUID = _.get(
    subjectStandardField,
    "interdisciplinaryUID"
  );
  const isSelected = !_.get(subjectStandardField, "hideByDefault", false);
  let label = _.lowerCase(_.get(subjectStandardField, "label", ""));

  return _.map(subjects, subject => {
    // turn off archived subjects by default
    const isArchived = _.get(subject, "isArchived", false);

    // store subjectId to filter out columns later based on that
    const subjectId = subject?.id;
    const isCourseSubject = _.find(courseSubjects, { id: subjectId });
    const isSelectedByDefault = isPYP ? true : isCourseSubject;
    const item = _.find(data, { subjectId, type: elementType });
    label = getElementTypeLabels({ t, type: elementType }) || label;
    const value = `${_.get(subject, "name", "")} ${label}`;
    const disabled =
      !isCourseSubject && enableAllFields
        ? false
        : item?.remainingCount + item?.coveredCount === 0;
    return {
      id: interdisciplinaryUID
        ? `${interdisciplinaryUID}_${subjectId}`
        : `${fieldUID}_${subjectId}`,
      subjectId,
      fieldUID,
      isSelected: isSelected && !isArchived && !disabled && isSelectedByDefault,
      value,
      disabled,
      disabledTooltip: t("unitPlan:configure_column_disabled_tooltip_text", {
        columnName: value,
      }),
    };
  });
};

const getInterdisciplinaryFieldColumns = ({
  field,
  subjects,
  t,
  data,
  elementType,
  courseSubjects,
  enableAllFields,
  isPYP,
}) => {
  const fieldUID = field?.fieldUID;
  const interdisciplinaryUID = _.get(field, "interdisciplinaryUID");
  const isSelected = _.includes(mypStandAloneDisciplinaryFields, fieldUID)
    ? false
    : !_.get(field, "hideByDefault", false);
  let label = _.lowerCase(_.get(field, "label", ""));
  const hideCoveredLabel = _.includes(mypStandAloneDisciplinaryFields, fieldUID)
    ? true
    : _.get(field, "hideCoveredLabel", false);

  return _.map(subjects, subject => {
    // turn off archived subjects by default
    const isArchived = _.get(subject, "isArchived", false);

    // store subjectId to filter out columns later based on that
    const subjectId = subject?.id;
    const isCourseSubject = _.find(courseSubjects, { id: subjectId });
    const isSelectedByDefault = isPYP ? true : isCourseSubject;

    const item = _.find(data, { subjectId, type: elementType });
    label = getElementTypeLabels({ t, type: elementType }) || label;
    const value = `${_.get(subject, "name", "")} ${label}`;

    const disabled =
      _.isEmpty(elementType) || (!isCourseSubject && enableAllFields)
        ? false
        : item?.remainingCount + item?.coveredCount === 0;

    return {
      id: interdisciplinaryUID
        ? `${interdisciplinaryUID}_${subjectId}`
        : `${fieldUID}_${subjectId}`,
      subjectId,
      fieldUID,
      isSelected: isSelected && !isArchived && !disabled && isSelectedByDefault,
      value,
      disabled,
      disabledTooltip: t("unitPlan:configure_column_disabled_tooltip_text", {
        columnName: value,
      }),
      hideCoveredLabel,
    };
  });
};

const isElementNodesEmpty = ({ data, fieldUID }) => {
  const item = _.find(data, { fieldUID });
  return item.remainingCount + item.coveredCount === 0;
};

export const getColumnOptions = ({
  courseOverviewFields,
  subjects,
  t,
  headerData,
  isCourseBasedGradingEnabled,
  courseSubjects,
  isLearningCoursesEnabled,
  isPYP,
}) => {
  const enableAllFields = getEnableAllFieldsInCourseOverview();
  const isNonPortedFieldsConvertedToPorted = shouldConvertNonPortedFieldsToPorted();
  return _.reduce(
    courseOverviewFields,
    (result, field) => {
      if (
        (isLearningCoursesEnabled &&
          _.includes(disciplinaryPlannerFields, field?.fieldUID)) ||
        _.get(field, "shouldDisplay", true)
      ) {
        const elementType = field?.elementType;
        const fieldUID = field?.fieldUID;

        // for myp disciplinaryFields, all columns are displayed separately
        if (
          _.includes(mypDisciplinaryFields, fieldUID) &&
          !isSubjectStandard({ elementType })
        ) {
          const interDisciplinaryFieldColumns = getInterdisciplinaryFieldColumns(
            {
              field,
              subjects,
              t,
              data: headerData,
              elementType,
              courseSubjects,
              enableAllFields,
              isPYP,
            }
          );
          result = _.concat(result, interDisciplinaryFieldColumns);
        }
        // for subject standard, all columns are displayed separately
        else if (
          isSubjectStandard({ elementType }) &&
          !_.isEqual(elementType, "DP_SUBJECT_STANDARD") &&
          !isCourseBasedGradingEnabled
        ) {
          const subjectStandardsColumns = getSubjectStandardColumns({
            subjectStandardField: field,
            subjects,
            t,
            data: headerData,
            elementType,
            courseSubjects,
            enableAllFields,
            isPYP,
          });

          result = _.concat(result, subjectStandardsColumns);
        } else {
          let value = _.get(field, "label", "");
          const elementType = _.get(field, "elementType");

          if (enableAllFields || isNonPortedFieldsConvertedToPorted) {
            const hideCoveredLabel = _.get(field, "hideCoveredLabel", false);
            const isNotPlannerElement = _.get(
              field,
              "isNotPlannerElement",
              false
            );
            // new fields
            if (isNotPlannerElement) {
              const isPortedField = _.get(field, "isPortedField", false);
              value = getElementTypeLabels({ t, type: elementType }) || value;

              result.push({
                id: field?.fieldUID,
                isSelected: !_.get(field, "hideByDefault", false),
                value,
                disabled: false,
                hideCoveredLabel,
                isPortedField,
                isNotPlannerElement,
              });
            } else {
              // planner element fields
              value = getElementTypeLabels({ t, type: elementType }) || value;

              result.push({
                id: field?.fieldUID,
                isSelected:
                  !_.get(field, "hideByDefault", false) &&
                  !isElementNodesEmpty({ data: headerData, fieldUID }),
                value,
                disabled: isElementNodesEmpty({ data: headerData, fieldUID }),
                disabledTooltip: t(
                  "unitPlan:configure_column_disabled_tooltip_text",
                  {
                    columnName: _.lowerCase(value),
                  }
                ),
              });
            }
          } else {
            value = getElementTypeLabels({ t, type: elementType }) || value;

            result.push({
              id: field?.fieldUID,
              isSelected:
                !_.get(field, "hideByDefault", false) &&
                !isElementNodesEmpty({ data: headerData, fieldUID }),
              value,
              disabled: isElementNodesEmpty({ data: headerData, fieldUID }),
              disabledTooltip: t(
                "unitPlan:configure_column_disabled_tooltip_text",
                {
                  columnName: _.lowerCase(value),
                }
              ),
            });
          }
        }
      }
      return result;
    },
    []
  );
};

const getNodesWithParentNodes = ({ filteredNodes, nodes }) => {
  return _.reduce(
    filteredNodes,
    (result, node) => {
      let iterator = node;

      while (iterator) {
        const isRoot = _.has(iterator, "parent") && _.isNull(iterator.parent);

        // don't push existing nodes
        if (!_.find(result, { id: iterator.id })) {
          result = [iterator, ...result];
        }

        // we've reached apex of the tree
        if (isRoot) break;

        iterator = _.find(nodes, { id: iterator.parent });
      }

      return result;
    },
    []
  );
};

export const getNodesWithParentNodesMemoized = _.memoize(
  params => getNodesWithParentNodes(params),
  props => JSON.stringify(props)
);

const getPYPYearlyInsightFilters = ({
  subjects,
  grades,
  curriculumProgramId,
}) => {
  return {
    subjects: _.map(subjects, "id"),
    grades,
    curriculumProgramId,
  };
};

export const getPYPYearlyInsightFiltersMemoized = _.memoize(
  params => getPYPYearlyInsightFilters(params),
  params => JSON.stringify(params)
);

const getTemplateFilters = ({ curriculumProgramId }) => {
  return {
    templateType: ["UNIT_PLAN"],
    curriculumProgramIds: curriculumProgramId ? [curriculumProgramId] : [],
  };
};

export const getTemplateFiltersMemoized = _.memoize(
  params => getTemplateFilters(params),
  params => JSON.stringify(params)
);

const getUpdatedFilter = ({ filters, courseId }) => {
  const updatedFilters = {
    ...filters,
    courses: [courseId],
    unitType: [],
  };
  return updatedFilters;
};

export const getUpdatedFilterMemoized = _.memoize(
  params => getUpdatedFilter(params),
  params => JSON.stringify(params)
);

export const areAllUnselected = _.memoize(({ columnOptions }) =>
  _.every(columnOptions, item => item.isSelected === false)
);

export const getEnableAllFieldsInCourseOverview = () => {
  return ACLStore.can("FeatureFlag:EnableAllFieldInCourseOverview");
};

export const RADIO_BUTTONS = [
  {
    label: "unitPlan:course_overview_download_with_all_units",
    value: "ALL",
  },
  {
    label: "unitPlan:course_overview_download_with_selected_units",
    value: "SELECTED",
  },
];

export const FIELD_TO_DEPENDENT_FIELDS_MAP = {
  theme: "focusAreas",
  globalContextMYP: "explorationDevelopMYP",
};

export const generateDependentFieldParamsForUpdate = ({
  dependentFieldOptions,
  dependentFieldUid,
}) => {
  switch (dependentFieldUid) {
    case "explorationDevelopMYP": {
      const value = _.map(dependentFieldOptions, option => {
        return { id: option.value, value: option.label, isSelected: false };
      });
      const params = {
        fieldUID: dependentFieldUid,
        selectedDataParams: value,
        selectedDataExtraParams: {},
        hasAddRemoveValues: false,
      };
      return params;
    }
    case "focusAreas": {
      const params = {
        fieldUID: dependentFieldUid,
        selectedDataParams: [],
        selectedDataExtraParams: dependentFieldOptions,
        hasAddRemoveValues: true,
      };
      return params;
    }
    default: {
      return {};
    }
  }
};
